import { Suspense, lazy, useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { faceApi } from "../services/faceApi";
import "./FaceVerification.css";

/* The AWS Face Liveness stage. LAZY on purpose: this is the only thing in the
   front end that pulls the AWS SDK, and loading it here puts it in its own
   chunk, fetched only when a visitor actually reaches this screen AND the
   backend has said liveness is on. Every other page's bundle is untouched. */
const AwsFaceLiveness = lazy(() => import("./liveness/AwsFaceLiveness"));

/* =====================================================
   FACE VERIFICATION  (AWS Rekognition)

   Sits between email verification and login:
       Register → Email verification → Continue → **here** → Login → Home

   THE ORDER, WHICH THE SERVER ENFORCES
       camera permission → live face check → result

     1. CAMERA      permission is established first, so no AWS session is spent
                    on someone who cannot use a camera. Nothing is shown or
                    photographed here.
     2. LIVE FACE   AWS Face Liveness: the browser streams a few seconds of
                    video straight to Rekognition, which decides whether a live
                    person is there. The server then does the matching, the
                    one-face-one-account check and the enrolment with the still
                    AWS took. That IS the whole verification — there is no
                    photo step. If the server has no live check configured, the
                    page says so rather than asking for a photo.

   WHAT THIS COMPONENT IS RESPONSIBLE FOR
     Asking for the camera, running the live face check, and turning every
     possible failure — camera, face, or network — into something a
     non-technical person can act on. It makes no judgement of its own about
     whether liveness passed: the server asks AWS, because a check the browser
     could talk itself out of is not a check at all. Finishing the stream proves
     nothing until the server has asked AWS what it decided.

   NO AWS CREDENTIAL IN THE BROWSER
     No key, no secret, no collection id — not in this file, not in the bundle.
     The liveness stage needs a region and a Cognito identity pool id, both
     public values, and both fetched at runtime from GET /api/face/config so
     they live in the backend .env with everything else.
===================================================== */

/* Where we remember who is mid-verification. Survives a refresh and a trip
   back to the login screen, which is what makes "leave and come back later"
   work without a broken or looping navigation. */
const PENDING_EMAIL_KEY = "pendingFaceEmail";

/* LIVE FACE ONLY (2026-09-18). The page used to fall back to a photo
   challenge (a button, a countdown and a snapshot) whenever the live check
   was unavailable, and showed its own camera preview around it. That
   photo path is gone: the AWS live face check is the whole verification, and
   if it cannot run the page says so instead of asking for a photo. Our own
   camera is still opened briefly, only to ask for camera permission. */
const LIVE_FACE_UNAVAILABLE = {
  message: "Live face verification is not available right now.",
  hint: "Please try again in a few minutes.",
  tone: "error",
};

/* What the person is told once the browser has permanently refused. The
   manager asked for exactly one simple sentence here, and no walkthrough of
   any particular browser's settings menu — those differ per browser and per
   version, and getting them wrong is worse than not attempting them. */
const BLOCKED_MESSAGE =
  "Camera access is blocked. Please allow Camera permission in your browser/site settings, then try again.";

/* ── THE THREE MOBILE TIMINGS ──
   A laptop webcam is opened and closed by one page at a time and is ready the
   instant getUserMedia resolves. A phone camera is neither: the OS owns it,
   only one consumer may hold it, and it is handed over and warmed up
   asynchronously. Every number below exists because of that difference, and
   each is a ceiling rather than a wait — nothing sits here for the full time
   unless the device really is that slow. */

/* How long the device is given to actually let go of the camera before the
   next consumer asks for it. `track.stop()` returns immediately; the hardware
   release behind it does not, and on Android Chrome and iOS Safari a
   getUserMedia issued inside that window is refused with NotReadableError. */
const CAMERA_RELEASE_MS = 350;

/* One retry when the camera answers "busy", for exactly that reason. */
const CAMERA_BUSY_RETRY_MS = 400;

/* How long a freshly opened stream is given to produce its first frame before
   we photograph it anyway. A phone can report a live track a good few hundred
   milliseconds before the sensor delivers a picture, and a canvas drawn in
   that window is a black rectangle. */
const FIRST_FRAME_TIMEOUT_MS = 800;

/* The events a video element fires as frames start arriving. Browsers disagree
   about which of these comes first — and Safari can reach a usable frame
   without firing `loadeddata` at all — so all of them are listened for and the
   real test is whether videoWidth has become non-zero. */
const FIRST_FRAME_EVENTS = ["loadedmetadata", "loadeddata", "canplay", "playing", "resize"];

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/* Resolve once the video is really showing something, or when the ceiling is
   reached — whichever comes first. Never rejects: a camera that is slow is not
   an error, and the caller's existing blank-frame guard is what decides what
   to do if no frame ever arrives. */
function waitForFirstFrame(video, timeoutMs) {
  if (!video) return Promise.resolve(false);

  const hasFrame = () => video.videoWidth > 0 && video.videoHeight > 0;
  if (hasFrame()) return Promise.resolve(true);

  return new Promise((resolve) => {
    let settled = false;

    const finish = () => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      FIRST_FRAME_EVENTS.forEach((name) => video.removeEventListener(name, onFrame));
      resolve(hasFrame());
    };

    const onFrame = () => {
      if (hasFrame()) finish();
    };

    const timer = setTimeout(finish, timeoutMs);
    FIRST_FRAME_EVENTS.forEach((name) => video.addEventListener(name, onFrame));
  });
}

/* The constraint ladder getUserMedia is tried with, most generous first. See
   openStream below for why there is a ladder at all and why nothing here is
   an `exact` constraint. */
const CAMERA_CONSTRAINTS = [
  { facingMode: { ideal: "user" }, width: { ideal: 1280 }, height: { ideal: 720 } },
  { facingMode: { ideal: "user" }, width: { ideal: 640 }, height: { ideal: 480 } },
  { facingMode: { ideal: "user" } },
  true,
];

/* Camera failures, in the words of the people they happen to. getUserMedia
   reports these through error.name, which is stable across browsers even
   though the messages are not. */
const CAMERA_ERRORS = {
  NotAllowedError: {
    title: "Camera permission is needed",
    message: "We need your camera to check that you're a real person.",
    hint: "Press 📷 Access Camera below and choose Allow.",
  },
  NotFoundError: {
    title: "No camera found",
    message: "This device doesn't seem to have a camera we can use.",
    hint: "Try again on a phone or laptop with a front camera.",
  },
  NotReadableError: {
    title: "Camera is already in use",
    message: "Another app or browser tab is using the camera right now.",
    hint: "Close any video call, camera app or other tab, then press Try again.",
  },
  OverconstrainedError: {
    title: "Camera not supported",
    message: "We couldn't start this device's camera at the size we need.",
    hint: "Please try again, or use a different device.",
  },
  SecurityError: {
    title: "Camera blocked for security",
    message: "Your browser only allows the camera on a secure (https) connection.",
    hint: "Please open the site over https and try again.",
  },
  /* Not a getUserMedia error name — ours. A phone browser opened over plain
     http (a laptop's LAN address, say) hides navigator.mediaDevices entirely,
     so the generic "this browser has no camera API" would be shown to someone
     whose browser is perfectly capable. This is the single most common reason
     a page that works on a laptop fails on a phone. */
  InsecureContext: {
    title: "Camera needs a secure connection",
    message: "Phone browsers only allow the camera on https pages.",
    hint: "Open this site with https:// (or on localhost) and try again.",
  },
  AbortError: {
    title: "The camera stopped unexpectedly",
    message: "Something interrupted the camera before it could start.",
    hint: "Please press Try again.",
  },
  Disconnected: {
    title: "The camera disconnected",
    message: "The camera was switched off or unplugged during the check.",
    hint: "Reconnect it, then press Try again.",
  },
  Unsupported: {
    title: "Camera not available in this browser",
    message: "This browser doesn't support camera access.",
    hint: "Please try Chrome, Safari, Edge or Firefox — or open the site on your phone.",
  },
  Unknown: {
    title: "We couldn't start the camera",
    message: "Something went wrong opening the camera.",
    hint: "Please press Try again.",
  },
};

function FaceVerification() {
  const navigate = useNavigate();
  const location = useLocation();

  /* ── who are we verifying ──
     Three sources, most trustworthy first: the state React Router carried over
     from the Register screen, a ?email= in the URL, and finally what we
     remembered locally. The last one is what survives a refresh. */
  const initialEmail = (() => {
    const fromState = location.state?.email;
    if (fromState) return String(fromState).trim().toLowerCase();
    const fromQuery = new URLSearchParams(location.search).get("email");
    if (fromQuery) return String(fromQuery).trim().toLowerCase();
    try {
      return (localStorage.getItem(PENDING_EMAIL_KEY) || "").trim().toLowerCase();
    } catch {
      return "";
    }
  })();

  const [email, setEmail] = useState(initialEmail);
  const [emailDraft, setEmailDraft] = useState("");

  /* ── the state machine ──
     "email"    we don't know whose face this is; ask
     "starting" opening the camera / asking the server for a session
     "camera"   the camera is unavailable — the page stays put and offers the
                📷 Access Camera button; it is NOT a separate screen
     "liveness" AWS Face Liveness (the live face check) is running
     "failed"   the live check did not pass; says why and offers another go
     "blocked"  a dead end (duplicate face, feature off) — no retry offered
     "done"     verified */
  /* "intro" is the camera-access screen every candidate lands on after the
     email code: nothing touches the camera until they press Allow. */
  const [phase, setPhase] = useState(initialEmail ? "intro" : "email");
  const [cameraError, setCameraError] = useState(null);
  /* Set once the browser has refused permission outright, which is the only
     state the person cannot get out of by pressing the button again. */
  const [permissionBlocked, setPermissionBlocked] = useState(false);
  const [notice, setNotice] = useState(null); // { message, hint, tone }
  const [, setProgress] = useState({ current: 0, total: 1 });
  const [busy, setBusy] = useState(false);

  /* ── the liveness stage ──
     `config` is what GET /api/face/config said (whether liveness runs at all,
     and the public region + identity pool the detector needs). `livenessId` is
     the AWS session the browser streams to; it is created by our server. */
  const [config, setConfig] = useState(null);
  const [livenessId, setLivenessId] = useState(null);

  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const sessionIdRef = useRef(null);
  /* Bumped on every camera start, so a getUserMedia promise that resolves after
     a newer start can tell that it is stale. See startCamera. */
  const cameraGeneration = useRef(0);
  /* Set on unmount. Every async continuation checks it before touching state,
     so navigating away mid-request can't warn about updating a dead component
     or leave the camera running. */
  const goneRef = useRef(false);
  /* Guards the liveness verdict call, since the detector can call back more
     than once. */
  const livenessChecked = useRef(false);
  /* Whether AWS has already cleared this attempt. Read by the resume path,
     which has to know which stage to come back to. */
  const livenessPassedRef = useRef(false);
  /* The current phase, readable from a listener that was installed once at
     mount and would otherwise only ever see the first render's value. */
  const phaseRef = useRef(phase);
  /* Why the last getUserMedia call failed, by error.name. */
  const lastCameraErrorRef = useRef(null);

  /* =========================
     CAMERA LIFECYCLE
  ========================= */

  /* Ask for the front camera, and be willing to take less.

     WHY A LADDER RATHER THAN ONE SET OF CONSTRAINTS
       A laptop webcam happily hands over 1280x720, and that is what the first
       rung asks for. A phone is a different machine: front cameras report
       portrait-shaped modes, browsers disagree about which combinations are
       satisfiable, and Android Chrome in particular throws
       OverconstrainedError for a request a laptop answers without blinking —
       which is exactly the "works on my laptop, fails on mobile" report this
       change is here to fix. Each rung below asks for less than the last, and
       the last asks for nothing at all, so a camera that exists is a camera we
       can open.

     WHY `ideal` AND NEVER `exact`
       An `ideal` constraint is a preference the browser may quietly ignore;
       `exact` is a demand it must refuse if it cannot meet. Nothing here needs
       a guaranteed resolution — the frame is downscaled before upload anyway —
       so nothing here demands one. facingMode is `ideal: "user"` too: on the
       handful of devices whose front camera is not labelled that way, a rear
       camera the person can turn round beats no camera at all. */
  const openStream = useCallback(async () => {
    let lastError = null;
    /* Once per camera open, not once per rung — four polite retries for a
       camera that is genuinely in another app would just be slow. */
    let retriedBusy = false;

    for (const video of CAMERA_CONSTRAINTS) {
      /* eslint-disable-next-line no-constant-condition */
      while (true) {
        try {
          return await navigator.mediaDevices.getUserMedia({ video, audio: false });
        } catch (err) {
          lastError = err;
          const name = err?.name;

          /* ── "BUSY", WHICH ON A PHONE OFTEN MEANS "NOT LET GO YET" ──
             A phone allows one consumer of the camera at a time and releases
             it asynchronously, so asking a fraction of a second after the
             previous holder stopped its tracks is refused — which is exactly
             what happens when the liveness stage hands the camera back to
             this page. A laptop, where two consumers are allowed and the
             release is immediate, never sees it. One retry after a beat turns
             that into a preview instead of "close your other apps". */
          if ((name === "NotReadableError" || name === "AbortError") && !retriedBusy) {
            retriedBusy = true;
            await wait(CAMERA_BUSY_RETRY_MS);
            continue;
          }

          /* Only a constraint the device could not satisfy is worth retrying
             with less. A refused permission, a camera in use or a missing camera
             will answer the same way however little we ask for, and retrying
             would only prompt the person four times over. */
          if (name !== "OverconstrainedError" && name !== "ConstraintNotSatisfiedError") throw err;
          break;
        }
      }
    }

    throw lastError;
  }, []);

  /* Give the camera back. The ORDER matters on a phone: the element is
     detached first, then the tracks are stopped.

     iOS Safari treats a media element that still holds the stream as a live
     consumer of the camera, so stopping the tracks while srcObject is still
     set can leave the device owned by a page that has stopped showing it — and
     the next thing to ask for the camera, which on this screen is the AWS
     liveness detector, is refused. Desktop browsers do not care about the
     order, which is why this only ever went wrong on a phone. */
  const stopCamera = useCallback(() => {
    const video = videoRef.current;
    if (video) video.srcObject = null;

    const stream = streamRef.current;
    if (stream) {
      stream.getTracks().forEach((t) => {
        t.onended = null;
        try {
          t.stop();
        } catch {
          /* the track was already gone */
        }
      });
      streamRef.current = null;
    }
  }, []);

  /* Hand the camera over to the AWS liveness detector.
     Stopping is not the same as having stopped: see CAMERA_RELEASE_MS. The
     beat costs a third of a second on the one screen transition that needs
     it, and is the difference between the detector opening the camera and
     reporting that it could not. */
  const releaseCameraForLiveness = useCallback(async () => {
    stopCamera();
    await wait(CAMERA_RELEASE_MS);
  }, [stopCamera]);

  const startCamera = useCallback(async () => {
    if (!navigator.mediaDevices?.getUserMedia) {
      /* A phone browser on a plain-http page has no mediaDevices AT ALL, which
         is indistinguishable from an ancient browser unless the page checks.
         Naming the real cause is the difference between a fixable problem and
         "it just doesn't work on my phone". */
      const insecure =
        typeof window !== "undefined" &&
        window.isSecureContext === false;
      setCameraError(insecure ? CAMERA_ERRORS.InsecureContext : CAMERA_ERRORS.Unsupported);
      setPhase("camera");
      return false;
    }

    stopCamera();

    /* getUserMedia can be in flight when another start is asked for — React
       StrictMode remounts this component in development, and a fast double-tap
       on Try again does the same in production. Without a generation token the
       first stream would still be adopted after the second replaced it, and its
       tracks would never be stopped: the camera light stays on with nothing
       showing it. A stale resolution stops its own stream and bails. */
    const generation = (cameraGeneration.current += 1);

    try {
      const stream = await openStream();

      if (goneRef.current || generation !== cameraGeneration.current) {
        stream.getTracks().forEach((t) => t.stop());
        return false;
      }

      streamRef.current = stream;

      /* A camera can vanish mid-check (unplugged, or another app grabs it).
         Without this the preview simply freezes and the person is left
         pressing a button that can never work. */
      stream.getVideoTracks().forEach((track) => {
        track.onended = () => {
          if (goneRef.current) return;
          setCameraError(CAMERA_ERRORS.Disconnected);
          setPhase("camera");
        };
      });

      const video = videoRef.current;
      if (video) {
        /* ── WHAT AN IPHONE NEEDS BEFORE IT WILL SHOW A STREAM ──
           Safari plays a video inline and without a gesture only when the
           element is muted AND marked playsinline, and it reads both as
           ATTRIBUTES at the moment the source is attached. React sets `muted`
           as a property, which is not guaranteed to be on the node before this
           line runs — the classic cause of a preview that stays black on an
           iPhone while working everywhere else. Setting both here costs
           nothing and removes the whole class of failure.
           `webkit-playsinline` covers the older iOS builds still in use. */
        video.muted = true;
        video.setAttribute("muted", "");
        video.setAttribute("playsinline", "");
        video.setAttribute("webkit-playsinline", "true");
        video.srcObject = stream;

        /* Safari on iOS rejects the promise if the element is detached mid-play;
           that is not an error worth showing anyone. */
        try {
          await video.play();
        } catch {
          /* autoplay was interrupted — the stream is still attached */
        }

        /* A phone reports a live track before the sensor delivers a picture.
           Waiting for the first real frame here means the preview the person
           is asked to pose in front of is a picture rather than a black box,
           and that anything captured afterwards has something in it. Bounded,
           and never fatal — a camera that produces nothing still lands on the
           existing blank-frame guard. */
        await waitForFirstFrame(video, FIRST_FRAME_TIMEOUT_MS);
        if (goneRef.current || generation !== cameraGeneration.current) return false;
      }

      setCameraError(null);
      setPermissionBlocked(false);
      lastCameraErrorRef.current = null;
      return true;
    } catch (err) {
      lastCameraErrorRef.current = err?.name || "Unknown";
      if (goneRef.current || generation !== cameraGeneration.current) return false;
      setCameraError(CAMERA_ERRORS[err?.name] || CAMERA_ERRORS.Unknown);
      setPhase("camera");
      return false;
    }
  }, [stopCamera, openStream]);

  /* What the browser currently thinks about the camera permission, when it is
     willing to say. Safari does not implement the query for "camera", and
     Firefox answers only sometimes, so "unknown" is a normal answer and never
     an error — it just means the question has to be asked by trying. */
  const permissionState = useCallback(async () => {
    try {
      if (!navigator.permissions?.query) return "unknown";
      const status = await navigator.permissions.query({ name: "camera" });
      return status?.state || "unknown";
    } catch {
      return "unknown";
    }
  }, []);

  /* =========================
     LIVENESS  (step 2 of camera → liveness → face verification)
  ========================= */

  /* Ask our server for an AWS liveness session for this attempt. The id it
     returns is what the detector streams against; our server keeps its own
     copy, and that copy is what the verdict is later read with. */
  const startLiveness = useCallback(async (faceSessionId) => {
    setBusy(true);
    setLivenessId(null);
    livenessChecked.current = false;
    livenessPassedRef.current = false;

    const created = await faceApi.createLiveness(faceSessionId);
    if (goneRef.current) return;

    setBusy(false);

    if (!created.ok) {
      setNotice({ message: created.message, hint: created.hint, tone: "error" });
      setPhase(created.retryable === false ? "blocked" : "failed");
      return;
    }

    /* The server says the live check is not configured. There is no photo
       fallback any more, so the person is told plainly instead. */
    if (created.liveness === false || !created.livenessSessionId) {
      stopCamera();
      setNotice(LIVE_FACE_UNAVAILABLE);
      setPhase("blocked");
      return;
    }

    setLivenessId(created.livenessSessionId);
  }, [stopCamera]);

  /* The detector says the stream has finished. That is ALL it says — the
     verdict comes from AWS, fetched by our server against the session id our
     server created. A browser that calls this without having streamed
     anything gets a refusal, not a pass. */
  const finishLiveness = useCallback(async () => {
    if (livenessChecked.current) return;
    livenessChecked.current = true;

    setBusy(true);
    setNotice(null);

    const verdict = await faceApi.checkLiveness(sessionIdRef.current);
    if (goneRef.current) return;

    setBusy(false);
    setLivenessId(null);

    if (!verdict.ok) {
      livenessChecked.current = false;
      setNotice({ message: verdict.message, hint: verdict.hint, tone: "error" });

      /* A dead end by design — this face already owns an account. No camera,
         no retry: the person is shown the way out instead. */
      if (verdict.retryable === false) {
        stopCamera();
        setPhase("blocked");
        return;
      }

      /* Liveness did not pass — a photograph, a screen, a face that left the
         frame. The button they are offered restarts the live check. */
      setPhase("failed");
      return;
    }

    livenessPassedRef.current = true;

    /* ── PASSED, AND THAT IS THE WHOLE CHECK ──
       The server has already done the matching, the one-face-one-account check
       and the enrolment, using the still AWS took during the stream. There is
       no second photograph to pose for: holding still for the liveness oval
       was the pose. */
    if (verdict.done) {
      stopCamera();
      try {
        localStorage.removeItem(PENDING_EMAIL_KEY);
      } catch {
        /* nothing to clean up */
      }
      setProgress(verdict.progress || { current: 1, total: 1 });
      setPhase("done");
      return;
    }

    /* A server that wants a photo after the live check belongs to the removed
       photo flow — nothing here can supply one, so the live check restarts. */
    livenessPassedRef.current = false;
    setNotice({
      message: "The live face check couldn't finish.",
      hint: "Please try again.",
      tone: "error",
    });
    setPhase("failed");
  }, [stopCamera]);

  /* The detector itself failed — no camera, an expired session, a network
     drop. Its own error screen is not shown; this page owns the wording, and
     a camera problem here must reach the same 📷 Access Camera button as one
     raised anywhere else. */
  const failLiveness = useCallback(
    async (err) => {
      const name = err?.name || err?.error?.name;
      const text = String(err?.message || err?.error?.message || "");

      setLivenessId(null);
      livenessChecked.current = false;

      if (name === "NotAllowedError" || /permission|denied/i.test(text)) {
        setCameraError(CAMERA_ERRORS.NotAllowedError);
        setPhase("camera");
        return;
      }

      setNotice({
        message: "The liveness check couldn't finish.",
        hint: "Please try again.",
        tone: "error",
      });
      setPhase("failed");
    },
    []
  );

  /* =========================
     SESSION
  ========================= */

  const beginSession = useCallback(
    async (targetEmail) => {
      setBusy(true);
      setNotice(null);

      const status = await faceApi.startSession(targetEmail);

      if (goneRef.current) return;

      if (status.ok && status.alreadyVerified) {
        /* Either the account is already through, or it was never gated. Both
           mean "go and log in" — and both are answered identically for an
           email that isn't registered at all, so this page can't be used to
           discover who has an account. */
        try {
          localStorage.removeItem(PENDING_EMAIL_KEY);
        } catch {
          /* private mode — nothing to clean up */
        }
        stopCamera();
        setNotice({ message: status.message || "This account is ready to log in.", tone: "ok" });
        setPhase("blocked");
        setBusy(false);
        return;
      }

      if (!status.ok) {
        setNotice({
          message: status.message,
          hint: status.hint,
          tone: "error",
        });
        setPhase(status.retryable === false ? "blocked" : "failed");
        setBusy(false);
        return;
      }

      sessionIdRef.current = status.sessionId;
      setProgress({ current: 0, total: 1 });
      setBusy(false);

      /* ── THE LIVE FACE CHECK ──
         The camera we just opened (to ask for permission) is handed over: the
         AWS detector runs its own preview, and two consumers of one camera is a
         guaranteed failure on a phone. */
      if (status.liveness) {
        await releaseCameraForLiveness();
        if (goneRef.current) return;
        setPhase("liveness");
        await startLiveness(status.sessionId);
        return;
      }

      /* No live check on this server — and no photo fallback any more. */
      stopCamera();
      setNotice(LIVE_FACE_UNAVAILABLE);
      setPhase("blocked");
    },
    [stopCamera, releaseCameraForLiveness, startLiveness]
  );

  /* Open the camera and the session together on first load.

     The CAMERA comes first, always — including on the liveness path, where the
     AWS detector would otherwise be the thing that asks for permission. Two
     reasons: a refused camera must not cost an AWS session, and permission
     trouble has to surface as this page's own 📷 Access Camera button rather
     than inside a third-party component we cannot reach into.

     The one case that skips our own camera is the liveness path when the
     browser has ALREADY granted permission: there is nothing left to ask, the
     detector is about to open the camera itself, and opening it here first
     only makes the preview flick on and off between the two stages. */
  const bootstrap = useCallback(
    async (targetEmail) => {
      setPhase("starting");
      setCameraError(null);
      setNotice(null);

      /* Asked once per visit, and before the camera, because the answer
         decides whether the camera is ours to open at all. A failure here is
         not fatal: no liveness stage simply means the original flow. */
      let settings = config;
      if (!settings) {
        settings = await faceApi.config();
        if (goneRef.current) return;
        setConfig(settings);
      }

      const skipOwnCamera =
        settings?.ok && settings.liveness === true && (await permissionState()) === "granted";
      if (goneRef.current) return;

      if (!skipOwnCamera) {
        const cameraReady = await startCamera();
        if (!cameraReady || goneRef.current) return;
      }

      await beginSession(targetEmail);
    },
    [startCamera, beginSession, permissionState, config]
  );

  useEffect(() => {
    goneRef.current = false;
    if (email) {
      try {
        localStorage.setItem(PENDING_EMAIL_KEY, email);
      } catch {
        /* private mode — the email just won't survive a refresh */
      }
      /* The check does NOT start here: the candidate is first shown the
         camera-access screen and starts it with "Allow camera access". */
    }
    return () => {
      goneRef.current = true;
      stopCamera();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [email]);

  /* Listeners installed once at mount read the phase through this ref. */
  useEffect(() => {
    phaseRef.current = phase;
  }, [phase]);

  /* ── PERMISSION GRANTED WHILE THE PAGE IS OPEN ──
     Someone who allows the camera in the browser's own settings, rather than
     through our button, should not have to press anything at all: the check
     picks itself up. Chromium fires this; Safari does not implement it, which
     is what the button is for. */
  useEffect(() => {
    let status;
    let cancelled = false;

    (async () => {
      try {
        if (!navigator.permissions?.query) return;
        status = await navigator.permissions.query({ name: "camera" });
        if (cancelled || !status) return;
        status.onchange = () => {
          if (goneRef.current || status.state !== "granted") return;
          setPermissionBlocked(false);
          if (phaseRef.current === "camera") resumeAfterPermission();
        };
      } catch {
        /* the browser will not answer the question — the button covers it */
      }
    })();

    return () => {
      cancelled = true;
      if (status) status.onchange = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* =========================
     ACTIONS
  ========================= */

  /* Everything the page does once the camera becomes available again, whether
     that was through the 📷 Access Camera button or the browser's own
     settings. It carries on from wherever the attempt had got to rather than
     starting over, so a permission wobble costs nothing. */
  const resumeAfterPermission = useCallback(async () => {
    /* Opening the camera IS the permission test — a browser that will not
       prompt rejects here — so this is also what the button reads to decide
       whether to explain that the block is permanent. */
    const ok = await startCamera();
    if (!ok || goneRef.current) return false;

    setNotice(null);

    /* No session yet — the camera was refused before one was opened. */
    if (!sessionIdRef.current) {
      await beginSession(email);
      return true;
    }

    /* A session exists: the live face check is where this attempt resumes. */
    await releaseCameraForLiveness();
    if (goneRef.current) return true;
    setPhase("liveness");
    await startLiveness(sessionIdRef.current);
    return true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startCamera, beginSession, email, startLiveness, releaseCameraForLiveness]);

  /* ── 📷 ACCESS CAMERA ──
     One button, two outcomes. Pressing it asks for the camera again; if the
     browser is willing to prompt, the person allows it and the check resumes
     on its own. If the browser has permanently refused, no amount of asking
     will produce a prompt, so it says so once, plainly, and stops pretending
     the button will help. */
  const requestCamera = async () => {
    setBusy(true);
    setPermissionBlocked(false);

    /* One attempt, not two: resuming opens the camera itself and reports
       whether the browser was willing. */
    const opened = await resumeAfterPermission();

    if (goneRef.current) return;
    setBusy(false);

    if (opened) return;

    const state = await permissionState();
    if (goneRef.current) return;

    /* "denied" is the browser saying it will not ask again. So is a
       NotAllowedError raised from a real button press in a browser that will
       not answer the query — Safari, in other words. */
    if (state === "denied" || lastCameraErrorRef.current === "NotAllowedError") {
      setPermissionBlocked(true);
    }
  };

  /* Another go at the liveness stage. AWS spends a session id per attempt and
     will not let one be read twice, so this asks for a new one rather than
     re-streaming to the old. */
  const retryLiveness = async () => {
    setNotice(null);
    await releaseCameraForLiveness();
    if (goneRef.current) return;
    setPhase("liveness");
    await startLiveness(sessionIdRef.current);
  };

  const restartCheck = async () => {
    sessionIdRef.current = null;
    setNotice(null);
    await bootstrap(email);
  };

  /* Leaving is explicitly allowed: the manager asked that people be able to
     step away and come back. Nothing is lost — the account keeps its
     "must verify" mark, the email is remembered, and trying to log in sends
     them straight back here. */
  const goBack = () => {
    stopCamera();
    navigate("/");
  };

  const submitEmail = (e) => {
    e.preventDefault();
    const value = emailDraft.trim().toLowerCase();
    if (!value) return;
    setEmail(value);
    setPhase("intro");
  };

  /* "Allow camera access" — the browser's permission prompt appears now, and
     the live face check follows as soon as it is allowed. */
  const allowCamera = () => {
    goneRef.current = false;
    bootstrap(email);
  };

  /* =========================
     RENDER HELPERS
  ========================= */

  /* The liveness detector replaces our preview while it runs — one camera,
     one consumer. */
  const showLiveness = phase === "liveness";

  /* ── WHEN THE 📷 BUTTON IS OFFERED ──
     Exactly while the camera is unavailable and the check is unfinished. It is
     not rendered on the verified screen, nor on a dead end, nor while the
     camera is working: a button that says "access camera" next to a live
     picture of the person's face is noise, and one that lingers after success
     invites a second, pointless permission prompt. */
  const showAccessCamera = phase === "camera";

  /* Where the candidate is in the whole signup — the same trail the email
     code page shows, so the two screens read as one flow. */
  const trail = (faceDone = false) => (
    <ol className="fv-steps" aria-label="Signup progress">
      <li className="fv-step fv-step-done">Register</li>
      <li className="fv-step fv-step-done">Verify email</li>
      <li className={`fv-step ${faceDone ? "fv-step-done" : "fv-step-active"}`}>Face</li>
      <li className="fv-step">Login</li>
    </ol>
  );

  const brand = <div className="fv-brand">✦ TheBlogsSphere</div>;

  /* Leaving is allowed (see goBack), but it is a quiet way out at the bottom,
     not a button competing with the check. */
  const backLink = (
    <div className="fv-foot">
      <button className="fv-link-btn" type="button" onClick={goBack}>
        ← Back to login
      </button>
    </div>
  );

  /* ── ask who this is (refresh with nothing remembered) ── */
  if (phase === "email") {
    return (
      <div className="fv-root">
        <div className="fv-card">
          {brand}
          <h1 className="fv-title">Face verification</h1>
          <p className="fv-sub">
            Enter the email you registered with, and we'll pick up where you left off.
          </p>
          <form onSubmit={submitEmail} className="fv-email-form">
            <label className="fv-label" htmlFor="fv-email">
              Email
            </label>
            <input
              id="fv-email"
              className="fv-input"
              type="email"
              placeholder="you@example.com"
              value={emailDraft}
              onChange={(e) => setEmailDraft(e.target.value)}
              autoFocus
              required
            />
            <button className="fv-btn fv-btn-primary" type="submit">
              Continue
            </button>
          </form>
          {backLink}
        </div>
      </div>
    );
  }

  /* ── camera access: the first thing after the email code ── */
  if (phase === "intro") {
    return (
      <div className="fv-root">
        <div className="fv-card">
          {brand}
          {trail()}

          <div className="fv-hero-icon" aria-hidden="true">
            <svg viewBox="0 0 24 24" width="30" height="30" fill="none" stroke="currentColor"
              strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
              <circle cx="12" cy="13" r="4" />
            </svg>
          </div>

          <h1 className="fv-title fv-center">Allow camera access</h1>
          <p className="fv-sub fv-center">
            Your email is verified. One last step: a quick live face check to confirm it's
            really you.
          </p>

          <div className="fv-panel">
            <p className="fv-panel-title">How it works</p>
            <ol className="fv-how">
              <li>
                <span className="fv-how-num">1</span>
                <span className="fv-how-text">
                  <strong>Allow the camera</strong>
                  <span>Your browser will ask — choose “Allow”.</span>
                </span>
              </li>
              <li>
                <span className="fv-how-num">2</span>
                <span className="fv-how-text">
                  <strong>Keep your face in the oval</strong>
                  <span>Look straight at the screen, in good light.</span>
                </span>
              </li>
              <li>
                <span className="fv-how-num">3</span>
                <span className="fv-how-text">
                  <strong>Keep still for a few seconds</strong>
                  <span>We confirm it's really you — then you're done.</span>
                </span>
              </li>
            </ol>
          </div>

          <div className="fv-actions">
            <button className="fv-btn fv-btn-primary" type="button" onClick={allowCamera} disabled={busy}>
              Allow camera access
            </button>
          </div>

          <p className="fv-privacy">
            Used only to verify your account. The camera turns off as soon as the check ends.
          </p>

          {backLink}
        </div>
      </div>
    );
  }

  /* ── verified ── */
  if (phase === "done") {
    return (
      <div className="fv-root">
        <div className="fv-card">
          {brand}
          {trail(true)}
          <div className="fv-result fv-result-ok">
            <div className="fv-result-icon">✓</div>
            <h1 className="fv-title">You're verified</h1>
            <p className="fv-sub">
              Your face check is complete. You can log in to your account now.
            </p>
            <button className="fv-btn fv-btn-primary" type="button" onClick={() => navigate("/")}>
              Continue to login
            </button>
          </div>
        </div>
      </div>
    );
  }

  /* ── dead end: duplicate face, or the feature is off ── */
  if (phase === "blocked") {
    const ok = notice?.tone === "ok";
    return (
      <div className="fv-root">
        <div className="fv-card">
          {brand}
          <div className={`fv-result ${ok ? "fv-result-ok" : "fv-result-stop"}`}>
            <div className="fv-result-icon">{ok ? "✓" : "!"}</div>
            <h1 className="fv-title">{ok ? "Nothing left to do" : "We can't verify this face"}</h1>
            <p className="fv-sub">{notice?.message}</p>
            {notice?.hint && <p className="fv-hint">{notice.hint}</p>}
            <button className="fv-btn fv-btn-primary" type="button" onClick={() => navigate("/")}>
              Go to login
            </button>
            <p className="fv-footnote">
              Need a hand? <Link className="fv-link" to="/terms">Read the terms</Link> or contact
              support.
            </p>
          </div>
        </div>
      </div>
    );
  }

  /* ── the live face check ──
     Camera trouble does not take the page over: the screen stays where it is
     and the 📷 Access Camera button appears under the camera area.

     WHY THE LIVENESS CLASSES EXIST
       The AWS detector lays its mobile screen out with `position: fixed`
       against the VIEWPORT. A card with a backdrop-filter on it becomes the
       containing block for exactly that kind of element, which trapped the
       detector inside this card and drew its oval at the wrong size on every
       phone. While the detector is on screen the card stops being that
       containing block; see the liveness block in FaceVerification.css. */
  return (
    <div className={`fv-root ${showLiveness ? "fv-root-liveness" : ""}`}>
      <div className={`fv-card ${showLiveness ? "fv-card-liveness" : ""}`}>
        {brand}
        {trail()}

        <h1 className="fv-title">Face verification</h1>
        <p className="fv-sub">
          Look into the camera and keep still — it takes about ten seconds.
        </p>

        <div
          className={`fv-stage ${showLiveness ? "fv-stage-liveness" : ""}`}
        >
          {/* ── STAGE 2: AWS FACE LIVENESS ──
              Mounted only while it is running, and only when the backend said
              it is configured. Its chunk (and the AWS SDK inside it) is
              fetched at this moment and never before. */}
          {showLiveness && livenessId && config?.identityPoolId ? (
            <Suspense
              fallback={
                <div className="fv-overlay">
                  <div className="fv-spinner" />
                  <p>Starting the liveness check…</p>
                </div>
              }
            >
              <AwsFaceLiveness
                sessionId={livenessId}
                region={config.region}
                identityPoolId={config.identityPoolId}
                onPassed={finishLiveness}
                onError={failLiveness}
              />
            </Suspense>
          ) : (
            <>
              {/* Our own camera is opened only to ask for permission before the
                  live check; it is never shown and nothing is photographed. */}
              <video
                ref={videoRef}
                className="fv-video"
                playsInline
                muted
                autoPlay
                style={{ visibility: "hidden" }}
              />
            </>
          )}

          {phase === "starting" && (
            <div className="fv-overlay">
              <div className="fv-spinner" />
              <p>Starting the camera…</p>
            </div>
          )}

          {showLiveness && !livenessId && (
            <div className="fv-overlay">
              <div className="fv-spinner" />
              <p>Preparing the liveness check…</p>
            </div>
          )}

          {/* The camera is unavailable. The stage says so in place of the
              picture, instead of sitting there black with no explanation. */}
          {phase === "camera" && (
            <div className="fv-overlay">
              <div className="fv-result-icon fv-overlay-icon">📷</div>
              <strong>{cameraError?.title || "Camera problem"}</strong>
              <p>{cameraError?.message}</p>
              {/* The hint tells the person to press the button. Once the
                  browser has refused for good it would be telling them to do
                  something that cannot work, so it gives way to the one
                  sentence under the button that explains why. */}
              {!permissionBlocked && cameraError?.hint && <p>{cameraError.hint}</p>}
            </div>
          )}

          {phase === "failed" && (
            <div className="fv-overlay">
              <div className="fv-result-icon fv-overlay-icon">↻</div>
              <p>Press Try again to restart the live face check.</p>
            </div>
          )}
        </div>

        {/* ── 📷 ACCESS CAMERA ──
            Small, directly below the verification area, and present only while
            the camera is unavailable. See showAccessCamera above. */}
        {showAccessCamera && (
          <div className="fv-camera-access">
            <button
              className="fv-btn fv-btn-camera"
              type="button"
              onClick={requestCamera}
              disabled={busy}
            >
              📷 Access Camera
            </button>
            {permissionBlocked && (
              <p className="fv-camera-blocked" role="status">
                {BLOCKED_MESSAGE}
              </p>
            )}
          </div>
        )}

        {notice && (
          <div className={`fv-notice fv-notice-${notice.tone || "error"}`} role="status">
            <strong>{notice.message}</strong>
            {notice.hint && <span>{notice.hint}</span>}
          </div>
        )}

        <div className="fv-actions">
          {phase === "failed" && (
            <>
              <button className="fv-btn fv-btn-primary" type="button" onClick={retryLiveness} disabled={busy}>
                Try again
              </button>
              <button className="fv-btn fv-btn-ghost" type="button" onClick={restartCheck} disabled={busy}>
                Start over
              </button>
            </>
          )}

          {phase === "liveness" && (
            <button className="fv-btn fv-btn-ghost" type="button" disabled>
              Checking you're really there…
            </button>
          )}
        </div>

        <ul className="fv-tips" aria-label="Tips">
          <li>Only you in the frame</li>
          <li>Good, even light</li>
          <li>No mask, cap or sunglasses</li>
        </ul>

        <p className="fv-footnote">
          You can leave and come back later — logging in brings you straight back here.
        </p>

        {backLink}
      </div>
    </div>
  );
}
export default FaceVerification;
